import { useCallback } from "react";

import useAuth from "./useAuth";
import useRequest from "./useRequest";

import { API_ALL, API_CONNECTED_OBJECT, BASE_ADMIN } from "../constants/api";
import { AllConnectedObjectType } from "../types/connected-object";
import { canAccessAdminDashboard } from "../utils/roles";
import { attachActiveFeature } from "../utils/features";

export type ObjectsListAssignment = "affected" | "nonAffected" | "both";

export type ObjectsListFilters = {
  term?: string;
  siteId?: string;
  status?: "" | "active" | "blocked";
  assignment?: ObjectsListAssignment;
  page?: number;
  limit?: number;
  orderby?: string;
  order?: "ASC" | "DESC";
};

const buildSearchParams = (filters: ObjectsListFilters = {}): Record<string, string> => {
  const limit = filters.limit && filters.limit > 0 ? Math.min(filters.limit, 100) : 50;
  const params: Record<string, string> = {
    page: String(filters.page ?? 1),
    limit: String(limit)
  };

  if (filters.term?.trim()) params.term = filters.term.trim();
  if (filters.siteId) params.siteId = filters.siteId;
  if (filters.status === "active" || filters.status === "blocked") {
    params.status = filters.status;
  }
  if (filters.assignment === "affected" || filters.assignment === "nonAffected") {
    params.assignment = filters.assignment;
  }
  if (filters.orderby) params.orderby = filters.orderby;
  if (filters.order) params.order = filters.order;

  return params;
};

const useListsObjects = () => {
  const { user } = useAuth();
  const canAccess = canAccessAdminDashboard(user?.role);

  const {
    error,
    isLoading,
    response,
    request
  } = useRequest<AllConnectedObjectType>();

  const fetchListObjects = useCallback((filters: ObjectsListFilters = {}) => {
    if (!canAccess) return;
    request(
      BASE_ADMIN,
      `${API_CONNECTED_OBJECT}/${API_ALL}`,
      {
        secure: true,
        searchParams: buildSearchParams(filters)
      }
    );
  }, [canAccess, request]);

  const data = response?.data ? attachActiveFeature(response.data) : null;

  return {
    isLoading,
    error,
    results: response,
    data,
    fetchListObjects
  };
};

export default useListsObjects;
